import { useEffect, useRef, useState, useMemo } from 'react';
import { preloadVideoUrls } from '@/services/videoPreloadService';
import ModeBar from '@/components/layout/ModeBar/ModeBar';
import HeroSection from '@/components/layout/HeroSection/HeroSection';
import VideoPlayer from '@/components/player/VideoPlayer/VideoPlayer';
import TranscriptPanel from '@/components/player/TranscriptPanel/TranscriptPanel';
import ContentRow from '@/components/content/ContentRow/ContentRow';
import MediaTile from '@/components/content/MediaTile/MediaTile';
import TrendingTile from '@/components/content/TrendingTile/TrendingTile';
import SearchResults from '@/components/search/SearchResults/SearchResults';
import MobileSearchBar from '@/components/ui/MobileSearchBar/MobileSearchBar';
import ShareModal from '@/components/ui/ShareModal/ShareModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { setMode } from '@/features/ui/uiSlice';
import { fetchLibrary, deleteSection, removeAssetFromSection } from '@/features/sections/sectionsSlice';
import { fetchHeroVideo, fetchStorageAsset } from '@/features/search/searchSlice';
import { seekToSeconds, setPlaying, togglePlay } from '@/features/player/playerSlice';
import type { MediaItem, TrendingItem } from '@/types';
import styles from './Pages.module.css';

const DEFAULT_TITLE = 'The Real Value of AI — Smarthub Demo';
const DEFAULT_DESC =
  'A candid discussion on how AI is compressing the pre-production cycle from weeks to hours — and why AI-generated video works best when it\'s invisible. Smart Hub indexed 7 segments with auto-detected timestamps.';

const LAST_ASSET_KEY = 'last_hero_asset_id';

export default function MediaIntelligencePage() {
  const dispatch = useAppDispatch();
  const [shareOpen, setShareOpen] = useState(false);
  const [activeChips, setActiveChips] = useState<string[]>(['all']);

  // Confirmation dialogs
  const [pendingDeleteSection, setPendingDeleteSection] = useState<{ id: string; label: string } | null>(null);
  const [pendingRemoveAsset, setPendingRemoveAsset] = useState<{ assetId: string; assetTitle: string; sectionId: string; sectionTitle: string } | null>(null);

  function toggleChip(id: string) {
    if (id === 'all') {
      setActiveChips(['all']);
      return;
    }
    setActiveChips((prev) => {
      const withoutAll = prev.filter((c) => c !== 'all');
      if (withoutAll.includes(id)) {
        const next = withoutAll.filter((c) => c !== id);
        return next.length === 0 ? ['all'] : next;
      }
      return [...withoutAll, id];
    });
  }

  const heroRef = useRef<HTMLDivElement>(null);

  const isPlaying = useAppSelector((s) => s.player.isPlaying);
  const { mediaRows } = useAppSelector((s) => s.content);
  const libraryLoading = useAppSelector((s) => s.sections.libraryLoading);
  const activeAsset = useAppSelector((s) => s.search.activeAsset);

  const handleHeroDownload = () => {
    console.log('Download clicked for asset:', activeAsset);
    const url = activeAsset?.download_url ?? activeAsset?.storage_url;
    if (!url) return;
    const fileName = activeAsset?.file_name ?? 'download.mp4';
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Build chip list: system sections first, then custom collections
  const systemCategories = useMemo(() => [
    { id: 'all', label: 'All' },
    ...mediaRows
      .filter((row) => !row.isUserCustom)
      .map((row) => ({ id: row.id, label: row.title })),
    ...mediaRows
      .filter((row) => row.isUserCustom)
      .map((row) => ({ id: row.id, label: row.title, isCustom: true as const })),
  ], [mediaRows]);

  const filteredRows = activeChips.includes('all')
    ? mediaRows
    : mediaRows.filter((row) => activeChips.includes(row.id));

  // Fetch live tab feeds on mount — each falls back silently to mock on error
  useEffect(() => {
    if (!activeAsset) {
      // Restore last watched video; fall back to latest from API if none saved
      const savedId = localStorage.getItem(LAST_ASSET_KEY);
      if (savedId) {
        dispatch(fetchStorageAsset(savedId));
      } else {
        dispatch(fetchHeroVideo());
      }
    }
    dispatch(fetchLibrary());
  }, [dispatch]);

  // Persist the last active asset id so it survives page refresh
  useEffect(() => {
    if (activeAsset?.asset_id) {
      localStorage.setItem(LAST_ASSET_KEY, activeAsset.asset_id);
    }
  }, [activeAsset?.asset_id]);

  // Scroll to the video player whenever a new asset is loaded
  const prevAssetId = useRef<string | null>(null);
  useEffect(() => {
    if (activeAsset?.asset_id && activeAsset.asset_id !== prevAssetId.current) {
      prevAssetId.current = activeAsset.asset_id;
      heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeAsset?.asset_id]);

  // Proactively buffer the first 5 s of every video in the visible rows so
  // hover previews play instantly from the HTTP cache instead of waiting for
  // a cold network request. Runs whenever rows are updated by the API.
  useEffect(() => {
    const urls: string[] = [];
    for (const row of mediaRows) {
      for (const item of row.items as (MediaItem | TrendingItem)[]) {
        if ((item as MediaItem).storage_url) urls.push((item as MediaItem).storage_url!);
      }
    }
    if (urls.length > 0) preloadVideoUrls(urls);
  }, [mediaRows]);

  // Derive hero content from active search asset, or fall back to defaults
  const heroTitle = activeAsset
    ? activeAsset.file_name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
    : DEFAULT_TITLE;

  // Safely coerce an all_sections item to a string.
  // The API may return { text, timestamp_ms } objects instead of plain strings.
  function sectionItemToString(item: unknown): string {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'text' in item) return (item as { text: string }).text;
    return String(item ?? '');
  }

  // summary is populated from segment description for storage assets;
  // fall back to first scene string, then default
  const rawScene0 = activeAsset?.all_sections?.scenes?.[0];
  const heroDesc =
    activeAsset?.summary ??
    (rawScene0 != null ? sectionItemToString(rawScene0) : null) ??
    DEFAULT_DESC;

  // For storage-tile assets query is '' — avoid showing '0 matched segments'
  const heroBadge = activeAsset
    ? activeAsset.query
      ? `${activeAsset.source_system} · ${activeAsset.matched_segments.length} matched segment${activeAsset.matched_segments.length !== 1 ? 's' : ''}`
      : activeAsset.source_system.replace(/_/g, ' ')
    : 'Spotlight · Indexed 2m ago';

  // Tags from key_points (preferred) or scenes when an asset is active
  const heroTags: string[] = activeAsset
    ? (activeAsset.all_sections?.key_points?.length
        ? activeAsset.all_sections.key_points
        : activeAsset.all_sections?.scenes ?? []
      ).slice(0, 8).map(sectionItemToString)
    : [];

  // Derive meta row from active asset
  const heroSpeakers = activeAsset?.all_sections?.ai_speakers?.map(sectionItemToString).join(', ');
  const heroDuration = (() => {
    const ms = activeAsset?.duration_ms;
    if (!ms) return null;
    const s = Math.round(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const ss = String(sec).padStart(2, '0');
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
  })();
  const heroMetaItems = activeAsset
    ? [
        ...(heroDuration ? [heroDuration, '·'] : []),
        ...(heroSpeakers ? [heroSpeakers] : ['1 speaker']),
        '·',
        activeAsset.source_system.replace(/_/g, ' '),
        '·',
        `${(activeAsset.file_size_mb ?? 0).toFixed(1)} MB`,
      ]
    : ['0:33', '·', '1 speaker', '·', '✓ Rights cleared', '·', 'MP4 source', '·', 'Indexed in 0m 28s'];

  return (
    <div>
      {/* Mobile search bar — only visible on ≤767px, sits above search results + tabs */}
      <MobileSearchBar />

      {/* Search results panel — shown when query ≥ 3 chars */}
      <SearchResults />

      {/* Intelligence mode tabs */}
      <div className={styles.intelligenceTabs}>
        <button
          className={`${styles.intelligenceTab} ${styles.intelligenceTabActive}`}
          onClick={() => dispatch(setMode('media'))}
        >
          Media Intelligence
        </button>
        <button
          className={`${styles.intelligenceTab} ${styles.intelligenceTabDisabled}`}
          disabled
        >
          Document Intelligence
          <span className={styles.comingSoonBadge}>Coming soon</span>
        </button>
      </div>

      {/* Hero */}
      <div ref={heroRef}>
      <HeroSection
        badgeText={heroBadge}
        badgeVariant="green"
        title={heroTitle}
        player={<VideoPlayer />}
        sidePanel={<TranscriptPanel />}
        description={heroDesc}
        tags={heroTags}
        meta={{ items: heroMetaItems }}
        actions={[
          {
            id: 'play-pause',
            label: isPlaying ? 'Pause' : 'Play from start',
            variant: 'primary',
            style: { width: '164px', justifyContent: 'center' },
            icon: isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" />
                <rect x="14" y="5" width="4" height="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            ),
            onClick: () => {
              if (isPlaying) {
                dispatch(togglePlay());
              } else {
                dispatch(seekToSeconds(0));
                dispatch(setPlaying(true));
              }
            },
          },
          { label: 'Share clip', variant: 'ghost', onClick: () => setShareOpen(true) },
          { label: 'Download', variant: 'ghost', onClick: handleHeroDownload },
        ]}
      />
      </div>

      {/* ModeBar tabs — sit between hero and content rows */}
      <ModeBar
        categories={systemCategories}
        activeChips={activeChips}
        onToggleChip={toggleChip}
        onDeleteChip={(id) => {
          const chip = systemCategories.find((c) => c.id === id);
          setPendingDeleteSection({ id, label: chip?.label ?? id });
        }}
      />

      {/* Content Rows */}
      <div className={styles.rows}>
        {filteredRows
          .map((row, index) => {
          const isFiltered = !activeChips.includes('all');
          return (
            <ContentRow
              key={row.id}
              row={row}
              defaultCollapsed={isFiltered ? false : index === 0}
              loading={libraryLoading}
            >
              {row.type === 'trending'
                ? (row.items as TrendingItem[]).map((item) => (
                    <TrendingTile key={item.id} item={item} />
                  ))
                : (row.items as MediaItem[]).map((item) => (
                    <MediaTile
                      key={item.id}
                      item={item}
                      onRemove={!row.isDynamic
                        ? () => setPendingRemoveAsset({
                            assetId: item.id,
                            assetTitle: (item as MediaItem).title ?? item.id,
                            sectionId: row.id,
                            sectionTitle: row.title,
                          })
                        : undefined
                      }
                    />
                  ))}
            </ContentRow>
          );
})}
      </div>

      {/* Share modal */}
      {shareOpen && (
        <ShareModal
          url={activeAsset?.storage_url ?? window.location.href}
          title={heroTitle}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Confirm: delete section */}
      {pendingDeleteSection && (
        <ConfirmDialog
          title="Delete collection?"
          message={<>Are you sure you want to delete <strong>{pendingDeleteSection.label}</strong>? This cannot be undone.</>}
          confirmLabel="Delete"
          onConfirm={() => {
            dispatch(deleteSection(pendingDeleteSection.id));
            // Remove from active chips if selected
            setActiveChips((prev) => {
              const next = prev.filter((c) => c !== pendingDeleteSection.id);
              return next.length === 0 ? ['all'] : next;
            });
            setPendingDeleteSection(null);
          }}
          onCancel={() => setPendingDeleteSection(null)}
        />
      )}

      {/* Confirm: remove asset from collection */}
      {pendingRemoveAsset && (
        <ConfirmDialog
          title="Remove from collection?"
          message={<>Remove <strong>{pendingRemoveAsset.assetTitle}</strong> from <strong>{pendingRemoveAsset.sectionTitle}</strong>?</>}
          confirmLabel="Remove"
          onConfirm={() => {
            dispatch(removeAssetFromSection({ assetId: pendingRemoveAsset.assetId, sectionId: pendingRemoveAsset.sectionId }));
            setPendingRemoveAsset(null);
          }}
          onCancel={() => setPendingRemoveAsset(null)}
        />
      )}
    </div>
  );
}
